import { MdFileDownload } from 'react-icons/md';
import { Button } from '@Components/Button';
import { SeriesToggleLegend } from '@Components/Charts/TrendLineChart/SeriesToggleLegend';
import { TrendLineChart } from '@Components/Charts/TrendLineChart/TrendLineChart';
import { TrendRow, TrendSeries } from '@Components/Charts/TrendLineChart/trend-line-chart-types';
import { Appearance, Size } from '@Components/designConstants';
import { DEFAULT_VISIBLE_TREND_BRANDS, TREND_CHART_HEIGHT } from '../../alexa-visibility-constants';
import { buildTrendExportRows } from '../../alexa-visibility-trend-utils';
import { useSeriesVisibility } from '../../hooks/useSeriesVisibility';
import { ExcelExportButton } from '../ExcelExportButton';

const PERCENT_TICKS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

interface TrendCardProps {
  // Also the element id the PNG download captures.
  id: string;
  title: string;
  // What the chart measures, shown beside the title.
  subtitle: string;
  rows: TrendRow[];
  // Every brand that can be drawn; the first is the selected brand.
  series: TrendSeries[];
  exportName: string;
  onDownloadPng: (id: string) => void;
}

// A brand-by-brand line chart over time. Each brand in the legend switches its line on and off.
export const TrendCard = ({
  id,
  title,
  subtitle,
  rows,
  series,
  exportName,
  onDownloadPng
}: TrendCardProps) => {
  const keys = series.map(item => item.key);
  const { visibleKeys, toggle } = useSeriesVisibility(keys, DEFAULT_VISIBLE_TREND_BRANDS);
  const visibleSeries = series.filter(item => visibleKeys.includes(item.key));

  return (
    <div className='aars-chart-card' id={id}>
      <div className='aars-chart-card-header'>
        <div className='aars-chart-card-title'>
          {title}
          <span className='aars-chart-card-subtitle'>{subtitle}</span>
        </div>
        <div className='aars-comp-citation-actions'>
          <ExcelExportButton
            outlined
            label='Excel'
            errorTitle="Couldn't export the trend"
            fileName={exportName}
            getSheets={() => [{ name: 'Trend', data: buildTrendExportRows(rows, series) }]}
          />
          <Button
            size={Size.SM}
            appearance={Appearance.OUTLINED}
            className='no-print'
            data-html2canvas-ignore='true'
            leadingElement={<MdFileDownload />}
            onClick={() => onDownloadPng(id)}
          >
            PNG
          </Button>
        </div>
      </div>
      <SeriesToggleLegend series={series} visibleKeys={visibleKeys} onToggle={toggle} />
      {visibleSeries.length === 0 ? (
        <div className='aars-trend-empty'>Turn on a brand above to see its trend.</div>
      ) : (
        <TrendLineChart
          rows={rows}
          series={visibleSeries}
          height={TREND_CHART_HEIGHT}
          yTicks={PERCENT_TICKS}
          fillFirstSeries
        />
      )}
    </div>
  );
};
